import { AutocompleteOption } from 'blip-ds/dist/types/components/autocomplete/autocomplete-select-interface';
import { ALL_GROUPS_ID, STORAGE_KEY, GROUP_NONE_ID } from 'constants/utils';
import { useAppContext } from 'contexts/AppContext';
import i18n from 'i18n/i18n';
import { InstallationState, UseInstallationsReturn } from 'interfaces';
import { useCallback, useEffect, useReducer, useState } from 'react';
import { createToastError } from 'services/Toast';
import { clearMultiSelect } from 'utils';
import { createGroups } from 'utils/groupHelpers';
import {
    buildInitialSelection,
    createNumberOptions,
    filterInstallationsByGroup,
    findSelectedInstallations,
    handleFetchError,
    pickInitialGroupId,
    setStoredGroupId,
    updateNumberOptionsWithSelection,
} from 'utils/installationHelpers';
import { InstallationAction } from '../types/InstallationActions';

const initialState: InstallationState = {
    groups: [],
    groupsOfGroups: new Map(),
    setGroupsOfGroups: () => undefined,
    selectedGroup: null,
    installations: [],
    appliedInstallations: [],
    selectedNumberOptions: [],
    numberOptions: [],
};

const installationReducer = (state: InstallationState, action: InstallationAction): InstallationState => {
    switch (action.type) {
        case 'INIT_WITH_DEFAULT_SELECTION':
            return {
                ...state,
                installations: action.payload.installations,
                groups: action.payload.groups,
                groupsOfGroups: action.payload.groupsOfGroups,
                selectedGroup: action.payload.selectedGroup,
                appliedInstallations: action.payload.appliedInstallations,
                selectedNumberOptions: action.payload.selectedNumberOptions,
                numberOptions: action.payload.numberOptions,
            };

        case 'SELECT_GROUP': {
            clearMultiSelect();

            const groupId = parseInt(action.payload.groupId);
            localStorage.setItem(STORAGE_KEY, groupId.toString());

            const groupInstallations = filterInstallationsByGroup(
                groupId,
                action.payload.installations,
                state.groupsOfGroups,
            );

            return {
                ...state,
                selectedGroup: groupId,
                appliedInstallations: [],
                selectedNumberOptions: [],
                numberOptions: createNumberOptions(groupInstallations),
            };
        }

        case 'CLEAR_GROUP_SELECTION':
            clearMultiSelect();
            return {
                ...state,
                selectedGroup: GROUP_NONE_ID,
                appliedInstallations: [],
                selectedNumberOptions: [],
                numberOptions: [],
            };

        case 'UPDATE_SELECTED_INSTALLATIONS': {
            const groupInstallations = state.selectedGroup
                ? filterInstallationsByGroup(state.selectedGroup, action.payload.installations, state.groupsOfGroups)
                : [];

            const selectedInstallationsAll = findSelectedInstallations(
                action.payload.numberOptions,
                action.payload.installations,
            );

            const selectedInstallations =
                state.selectedGroup === ALL_GROUPS_ID
                    ? selectedInstallationsAll
                    : selectedInstallationsAll.filter(installation =>
                          groupInstallations.some(group => group.installationId === installation.installationId),
                      );

            const availableInstallations =
                state.selectedGroup === ALL_GROUPS_ID ? action.payload.installations : groupInstallations;

            const updatedNumberOptions = updateNumberOptionsWithSelection(
                availableInstallations,
                action.payload.numberOptions,
            );

            return {
                ...state,
                selectedNumberOptions: selectedInstallations,
                numberOptions: updatedNumberOptions,
                appliedInstallations: selectedInstallations,
            };
        }

        default:
            return state;
    }
};

export const useInstallations = (): UseInstallationsReturn => {
    const {
        appServices: { goPersonalService, packsService },
    } = useAppContext();

    const [installationsState, dispatch] = useReducer(installationReducer, initialState);
    const [isBotsLoading, setIsBotsLoading] = useState(true);

    const { groups, selectedGroup, installations, selectedNumberOptions, appliedInstallations, numberOptions } =
        installationsState;

    const initializeInstallations = useCallback(async (): Promise<void> => {
        try {
            const fetchedInstallations = await goPersonalService.getInstallations();

            if (!fetchedInstallations?.length) {
                createToastError(
                    i18n.t('go-personal-error-no-numbers-title'),
                    i18n.t('go-personal-error-no-numbers-message'),
                );
                return;
            }

            const fetchedGroups = await packsService.getWorkGroups();
            const { groups: builtGroups, groupsOfGroups } = createGroups(fetchedGroups.workGroups);

            const groupIds = builtGroups.map(group => Number(group.value));
            const initialGroupId = pickInitialGroupId(groupIds);

            if (initialGroupId !== null) {
                setStoredGroupId(initialGroupId);
            }

            const { groupInstallations, initialNumberOptions } = buildInitialSelection(
                initialGroupId,
                fetchedInstallations,
                groupsOfGroups,
            );

            dispatch({
                type: 'INIT_WITH_DEFAULT_SELECTION',
                payload: {
                    installations: fetchedInstallations,
                    groups: builtGroups,
                    groupsOfGroups,
                    selectedGroup: initialGroupId,
                    appliedInstallations: groupInstallations,
                    selectedNumberOptions: groupInstallations,
                    numberOptions: initialNumberOptions,
                },
            });
        } catch (error) {
            handleFetchError(error, 'fetching installations');
        } finally {
            setIsBotsLoading(false);
        }
    }, [goPersonalService, packsService]);

    useEffect(() => {
        initializeInstallations();
    }, [initializeInstallations]);

    const handleChangeSelectedGroup = useCallback(
        (value: string): void => {
            const groupId = parseInt(value);
            const group = groups.find(g => g.value === groupId);

            if (group) {
                dispatch({
                    type: 'SELECT_GROUP',
                    payload: { groupId: group.value.toString(), installations },
                });
            } else if (value === '') {
                dispatch({ type: 'CLEAR_GROUP_SELECTION' });
            }
        },
        [groups, installations],
    );

    const handleMultiSelectedChange = useCallback(
        (numberOptions: AutocompleteOption[]): void => {
            dispatch({
                type: 'UPDATE_SELECTED_INSTALLATIONS',
                payload: { numberOptions, installations },
            });
        },
        [installations],
    );

    return {
        installations,
        appliedInstallations,
        selectedNumberOptions,
        numberOptions,
        isBotsLoading,
        selectedGroup,
        groups,
        handleChangeSelectedGroup,
        handleMultiSelectedChange,
    };
};
